import type { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";

import { Tooltip } from "@analyticsMD/quil";

import {
  ARAS_SCORE_LEGEND_LINES,
  type ArasScoreLegendLine,
  isArasScoreRowId,
} from "./aras-score-legend-lines";
import {
  ARAS_DIVIDER_LINE,
  ARAS_FONT_PRIMARY,
  arasPopoverMuiTypographyPrimarySx,
} from "./aras-score-popover-tokens";
import type { ArasScoreCategoryRow } from "./aras-score-types";
import { ARAS_POPOVER_PAPER_MAX_WIDTH } from "./suggestion-detail-popover-shell";

const ArasScoreLegendFromLines: FC<{
  lines: readonly ArasScoreLegendLine[];
}> = ({ lines }) => (
  <Box
    sx={{
      textAlign: "left",
      color: grey[900],
      fontSize: `${ARAS_FONT_PRIMARY} !important`,
      lineHeight: 1.45,
    }}
  >
    {lines.map((line, index) => (
      <Box
        key={line.value}
        sx={{ marginBottom: index < lines.length - 1 ? "0.5rem" : 0 }}
      >
        <Box component="span" sx={{ fontWeight: 700 }}>
          {line.value}
        </Box>
        <Box component="span"> — {line.text}</Box>
      </Box>
    ))}
  </Box>
);

const pointLineSx = {
  fontSize: `${ARAS_FONT_PRIMARY} !important`,
  color: grey[900],
  lineHeight: 1.45,
};

const MEDICAL_LEGEND_FALLBACK = ARAS_SCORE_LEGEND_LINES["medical-complexity"];

/** Default tooltip when a row has no catalog entry; one line for this score using medical copy, else full medical legend. */
export function defaultArasScoreTooltipContent(points: number): ReactNode {
  const line = MEDICAL_LEGEND_FALLBACK.find((l) => l.value === points);
  if (line) {
    return (
      <Box sx={pointLineSx}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {line.value}
        </Box>
        {" — "}
        {line.text}
      </Box>
    );
  }
  return <ArasScoreLegendFromLines lines={MEDICAL_LEGEND_FALLBACK} />;
}

export const scoreTooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: ARAS_POPOVER_PAPER_MAX_WIDTH,
      bgcolor: "#fff",
      color: grey[900],
      border: ARAS_DIVIDER_LINE,
      borderRadius: "0.375rem",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
      padding: "0.75rem 0.875rem",
      fontSize: `${ARAS_FONT_PRIMARY} !important`,
      lineHeight: 1.45,
      ...arasPopoverMuiTypographyPrimarySx,
    },
  },
  popper: {
    sx: {
      zIndex: 2000,
    },
  },
};

const scoreDigitSx = (color: string) => ({
  fontWeight: 700,
  fontSize: `${ARAS_FONT_PRIMARY} !important`,
  color,
  lineHeight: 1,
});

export const ArasScoreCellTooltip: FC<{
  score: number;
  color: string;
  /** Ignored when `disableTooltip` is true. */
  title?: ReactNode;
  ariaLabel: string;
  /** When true, score is not interactive and no tooltip is shown. */
  disableTooltip?: boolean;
}> = ({ score, color, title, ariaLabel, disableTooltip }) => {
  const digit = (
    <Box component="span" sx={scoreDigitSx(color)}>
      {score}
    </Box>
  );

  if (disableTooltip) {
    return (
      <Box
        component="span"
        aria-label={ariaLabel}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minWidth: "1.5rem",
        }}
      >
        {digit}
      </Box>
    );
  }

  return (
    <Tooltip
      title={title}
      placement="left"
      enterDelay={250}
      slotProps={scoreTooltipSlotProps}
    >
      <Box
        component="span"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={ariaLabel}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minWidth: "1.5rem",
          cursor: "help",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textUnderlineOffset: "2px",
        }}
      >
        {digit}
      </Box>
    </Tooltip>
  );
};

export function resolveRowScoreTooltip(row: ArasScoreCategoryRow): ReactNode {
  if (row.scoreTooltip != null) {
    return row.scoreTooltip;
  }
  const legendKey =
    row.legendRowId != null
      ? row.legendRowId
      : isArasScoreRowId(row.id)
        ? row.id
        : null;
  if (legendKey != null) {
    return (
      <ArasScoreLegendFromLines lines={ARAS_SCORE_LEGEND_LINES[legendKey]} />
    );
  }
  return defaultArasScoreTooltipContent(row.points);
}
